import { useStore } from '@nanostores/react';
import { useLoaderData } from '@remix-run/react';
import { motion } from 'framer-motion';
import { memo, useEffect, useRef, useState } from 'react';
import { LegacyIconButton } from '~/components/ui/LegacyIconButton';
import { UpgradePlanDialogs, type UpgradePlanCardType } from '~/components/ui/pricing/UpgradePlanDialogs';
import { type IndexLoaderData } from '~/layouts/IndexLayout';
import { PLAN_LEVEL_MAP, type PlanLevel } from '~/lib/.client/plans';
import { SPECIAL_TOKENS_NAME } from '~/lib/constants';
import { chatStore } from '~/lib/stores/chat';
import { userStore } from '~/lib/stores/user';
import { classNames } from '~/utils/classNames';
import { cubicEasingFn } from '~/utils/easings';
import { computeTokensLeft, isCloseToMax } from '~/utils/tokens-stats';

function dismiss() {
  chatStore.subscribeDismissed.set(true);
  chatStore.setSpecialTokenPromptVisible(false);
}

export const PromptSubscribeBanner = memo(() => {
  const contentDiv = useRef<HTMLDivElement>(null);

  const [contentHeight, setContentHeight] = useState(0);

  const { tokenStats: initialStats } = useLoaderData<IndexLoaderData>();

  const tokenStats = useStore(chatStore.tokenStats);
  const user = useStore(userStore.user);
  const isLoggedIn = user !== undefined;

  const started = useStore(chatStore.started);
  const subscribeDismissed = useStore(chatStore.subscribeDismissed);
  const forceShowTokenUsage = useStore(chatStore.forceShowTokenInChat);
  const forceShowSubscribeBannerInChat = useStore(chatStore.forceShowSubscribeBannerInChat);
  const specialTokenPromptVisibleSetting = useStore(chatStore.specialTokenPromptVisibleSetting);
  const tier = useStore(userStore.getTierStore());
  const activeOrganization = useStore(userStore.getActiveOrganization());

  useEffect(() => {
    if (initialStats && !chatStore.tokenStats.get()) {
      chatStore.tokenStats.set(initialStats);
    }

    if (contentDiv.current) {
      setContentHeight(contentDiv.current.getBoundingClientRect().height - 1);

      const observer = new ResizeObserver(([entry]) => {
        setContentHeight(entry.target.getBoundingClientRect().height - 1);
      });

      observer.observe(contentDiv.current);

      return () => observer.disconnect();
    }

    return undefined;
  }, [contentDiv.current]);

  const {
    closeToMax,
    period,
    remainingTokens = 0,
  } = computeTokensLeft(tokenStats, tier, tier?.plan !== 'personal' ? 'monthly' : undefined);

  const hasSpecialTokens =
    tokenStats?.specialTokens && tokenStats.specialTokens.available > 0 && !forceShowSubscribeBannerInChat;

  const hasRemainingSpecialTokensLeft =
    tokenStats?.specialTokens && tokenStats.specialTokens.available > tokenStats.specialTokens.used && hasSpecialTokens;

  // we only show the special tokens if the setting is true, and there are tokens available
  const canShowSpecialTokens = specialTokenPromptVisibleSetting && hasSpecialTokens;

  /**
   * We show the special tokens if:
   *  - First time and not first message sent (`specialTokenPromptVisibleSetting`)
   *  - There are special tokens left AND
   *      - `forceShowTokenUsage` is true OR
   *      - The tokens left are close to be emptied
   */
  const shouldShowSpecialTokens =
    canShowSpecialTokens ||
    ((forceShowTokenUsage || (tokenStats?.specialTokens && isCloseToMax(tokenStats.specialTokens))) &&
      hasRemainingSpecialTokensLeft);

  /**
   * An important point here:
   *
   * We should not show this banner if there are special tokens left even if the
   * existing tokens are close to the limit.
   */
  const visible =
    forceShowTokenUsage ||
    (started &&
      !subscribeDismissed &&
      !!tokenStats &&
      (!!closeToMax || canShowSpecialTokens) &&
      (shouldShowSpecialTokens || !hasRemainingSpecialTokensLeft));

  const canSubscribe = !!tokenStats?.nextTier || forceShowSubscribeBannerInChat;
  const nextTier = tokenStats?.nextTier?.type;
  const nextLevel = tokenStats?.nextTier?.level ?? 0;

  const [isUpgradePlanOpen, setIsUpgradePlanOpen] = useState(false);

  const increaseFactor = tokenStats?.nextTier
    ? Math.max(
        Math.floor(tokenStats.nextTier.limits.perDay / tokenStats.maxPerDay),
        Math.floor(tokenStats.nextTier.limits.perMonth / tokenStats.maxPerMonth),
      )
    : 1;

  if (!isLoggedIn) {
    return null;
  }

  return (
    <motion.div
      transition={{ duration: 0.2, ease: cubicEasingFn }}
      animate={{ height: visible ? contentHeight : 0 }}
      className="relative"
    >
      <div
        ref={contentDiv}
        className={classNames(
          'flex flex-wrap justify-between py-1 px-2 -top-px absolute transition-opacity duration-350 rounded-t-lg text-xs truncate border left-2 w-[calc(100%-1rem)] ',
          started
            ? 'border-b-0 border-bolt-elements-borderColor bg-bolt-elements-prompt-subscribeButton-background backdrop-blur'
            : 'dark:bg-[rgba(31,31,31,0.95)] bg-white backdrop-blur-[7px] mt-[1px] dark:border-bolt-elements-borderColor border-[#00000033]',
          {
            'opacity-0': !visible,
            'opacity-100': visible,
          },
        )}
      >
        {shouldShowSpecialTokens && tier && tier.plan !== 'personal' ? (
          <>
            {hasSpecialTokens && (
              <>
                <span className="text-bolt-elements-textPrimary">
                  {formatTokens(remainingTokens)} +{' '}
                  {formatTokens(tokenStats.specialTokens!.available - tokenStats.specialTokens!.used)}{' '}
                  {SPECIAL_TOKENS_NAME} tokens remaining.
                </span>
                <span
                  className={classNames('flex flex-row gap-1 text-bolt-elements-textPrimary', {
                    'mr-4': !forceShowTokenUsage,
                  })}
                >
                  Enjoy {formatTokens(tokenStats.specialTokens!.available)} {SPECIAL_TOKENS_NAME} free tokens!
                </span>
              </>
            )}
          </>
        ) : (
          <>
            {remainingTokens <= 0 ? (
              <span>You've used all your tokens.</span>
            ) : (
              <span>
                {formatTokens(remainingTokens)} {canSubscribe ? period : ''} tokens remaining.
              </span>
            )}
            {canSubscribe ? (
              <button
                className={classNames(
                  'bg-transparent font-medium inline-block text-bolt-elements-link hover:underline',
                  {
                    'mr-4': !forceShowTokenUsage,
                  },
                )}
                onClick={() => {
                  setIsUpgradePlanOpen(true);
                }}
              >
                Switch to {formatNextTier(nextTier, nextLevel)} for{' '}
                {Number.isFinite(increaseFactor) && increaseFactor > 1 ? `${increaseFactor}x` : ''} more usage
              </button>
            ) : (
              <button
                className={classNames(
                  'bg-transparent font-medium inline-block mr-4 text-bolt-elements-link hover:underline',
                  {
                    'mr-4': !forceShowTokenUsage,
                  },
                )}
                onClick={() => {
                  setIsUpgradePlanOpen(true);
                }}
              >
                Buy more
              </button>
            )}
            <UpgradePlanDialogs
              isOpen={isUpgradePlanOpen}
              cards={['teams', ...(!activeOrganization ? ['pro' as UpgradePlanCardType] : [])]}
              onClose={() => {
                setIsUpgradePlanOpen(false);
              }}
            />
          </>
        )}
        {!forceShowTokenUsage && (
          <LegacyIconButton onClick={dismiss} icon="i-ph:x" size="xs" className="absolute top-1 right-1" />
        )}
      </div>
    </motion.div>
  );
});

function formatTokens(count: number | undefined): string {
  if (typeof count === 'undefined') {
    return '';
  }

  if (count >= 1_000_000) {
    return `${(count / 1_000_000).toFixed(1)}M`;
  }

  if (count >= 1_000) {
    return `${Math.floor(count / 1_000)}K`;
  }

  return count.toString(10);
}

function formatNextTier(planName: string | undefined, level: number): string | undefined {
  if (!planName) {
    return undefined;
  }

  const { name, namePrice = '' } = PLAN_LEVEL_MAP[`${planName}_${level}` as PlanLevel] ?? {};

  // soft fallback in case of bug
  if (!name) {
    return `${planName.charAt(0).toUpperCase()}${planName.slice(1)}`;
  }

  return `${name} ${namePrice}`;
}
